/**
 * ConvertInteractionToLegalProcessModal
 * Modal for converting an interaction directly to a legal process
 */

import { useState, useEffect } from 'react';
import { FileText, Scale, AlertCircle, CheckCircle2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { interactionService, type ConvertToLegalProcessData } from '@/services/interaction-service';
import toast from '@/lib/toast';

import { getErrorMessage } from '../../utils/error.utils'
export interface ConvertInteractionToLegalProcessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (newLegalProcess: { id: string }) => void;
  interaction: {
    id: string;
    subject: string;
    description?: string;
  } | null;
}

export function ConvertInteractionToLegalProcessModal({
  isOpen,
  onClose,
  onSuccess,
  interaction
}: ConvertInteractionToLegalProcessModalProps) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<ConvertToLegalProcessData>({
    courtNumber: '',
    courtName: '',
    judgeName: '',
    practiceArea: '',
    caseType: ''
  });

  // Reset form when modal opens/interaction changes
  useEffect(() => {
    if (isOpen && interaction) {
      setFormData({
        courtNumber: '',
        courtName: '',
        judgeName: '',
        practiceArea: '',
        caseType: ''
      });
    }
  }, [isOpen, interaction]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!interaction || !formData.courtNumber.trim()) {
      toast.error('Número do processo judicial é obrigatório');
      return;
    }

    setLoading(true);
    try {
      const response = await interactionService.convertToLegalProcess(
        interaction.id,
        formData
      );

      toast.success('Atendimento convertido em Processo Judicial com sucesso!');

      if (onSuccess && typeof response.data?.id === 'string') {
        onSuccess({ id: response.data.id });
      }

      handleClose();
    } catch (error: unknown) {
      toast.error(
        getErrorMessage(error, 'Erro ao converter atendimento em processo judicial')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      courtNumber: '',
      courtName: '',
      judgeName: '',
      practiceArea: '',
      caseType: ''
    });
    onClose();
  };

  const handleChange = (field: keyof ConvertToLegalProcessData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent size="lg" fullScreenOnMobile className="max-sm:overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Converter Atendimento em Processo Judicial</DialogTitle>
          <DialogDescription>
            Para clientes que já possuem processo em andamento
          </DialogDescription>
        </DialogHeader>
        {interaction && (
          <div className="rounded-lg border bg-muted/40 p-3">
            <div className="flex items-start gap-3">
              <FileText className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <h4 className="mb-1 font-medium">Atendimento de Origem</h4>
                <p className="break-words text-sm text-muted-foreground">
                  <strong>Assunto:</strong> {interaction.subject}
                </p>
                {interaction.description && (
                  <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
                    <strong>Descrição:</strong> {interaction.description}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="rounded-lg border p-3">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <div className="text-sm">
              <p className="mb-1 font-medium">Conversão direta para Processo Judicial</p>
              <p className="text-muted-foreground">
                Use esta opção quando o cliente chega com um processo judicial já em andamento,
                pulando a etapa de &quot;Caso&quot;.
              </p>
            </div>
          </div>
        </div>

        <div role="note" className="rounded-lg border bg-muted/40 p-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <p className="text-sm text-muted-foreground">
              Após a conversão, o atendimento não poderá mais ser editado e aparecerá como
              &quot;Convertido em Processo Judicial&quot; na lista de atendimentos.
            </p>
          </div>
        </div>

        <form id="convert-to-legal-process-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="convert-legal-court-number">Número do Processo Judicial <span className="text-destructive">*</span></Label>
              <Input
                id="convert-legal-court-number"
                value={formData.courtNumber}
                onChange={(e) => handleChange('courtNumber', e.target.value)}
                placeholder="Ex: 0000000-00.0000.0.00.0000"
                required
              />
              <p className="text-xs text-muted-foreground">Número do processo no tribunal</p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="convert-legal-court-name">Nome do Tribunal</Label>
              <Input
                id="convert-legal-court-name"
                value={formData.courtName}
                onChange={(e) => handleChange('courtName', e.target.value)}
                placeholder="Ex: Tribunal de Justiça de SP"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="convert-legal-judge-name">Nome do Juiz</Label>
              <Input
                id="convert-legal-judge-name"
                value={formData.judgeName}
                onChange={(e) => handleChange('judgeName', e.target.value)}
                placeholder="Ex: Dr. João da Silva"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="convert-legal-area">Área de Atuação</Label>
              <Input
                id="convert-legal-area"
                value={formData.practiceArea}
                onChange={(e) => handleChange('practiceArea', e.target.value)}
                placeholder="Ex: Trabalhista, Cível, etc."
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="convert-legal-type">Tipo de Processo</Label>
              <Input
                id="convert-legal-type"
                value={formData.caseType}
                onChange={(e) => handleChange('caseType', e.target.value)}
                placeholder="Ex: Ação Trabalhista, etc."
              />
            </div>
          </div>

          <div className="rounded-lg border p-3">
            <div className="flex items-start gap-3">
              <Scale className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <div>
                <h4 className="mb-1 font-medium">O que será criado:</h4>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li>• Um novo processo judicial com as informações fornecidas</li>
                  <li>• Vínculo automático com o atendimento original</li>
                  <li>• Evento na timeline do cliente</li>
                  <li>• Badge de origem &quot;De Atendimento&quot; no processo</li>
                  <li>• Marcado como processo judicial (não aparece em &quot;Casos&quot;)</li>
                </ul>
              </div>
            </div>
          </div>
        </form>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            form="convert-to-legal-process-form"
            disabled={loading || !formData.courtNumber.trim()}
          >
            {loading ? 'Convertendo...' : 'Converter em Processo Judicial'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
